import type {
  AttemptResult, FurthestStep, KnownPortalOverview, PortalAttempts, PortalCve, PortalFigures, PortalGroup, PortalItem,
  PortalOverview, PortalStep,
} from "@/types";
import { ATTEMPT_ORDER, FURTHEST_STEP_ORDER, sourcesHref, WHO_ORDER } from "@/utils/sourcesFilters.ts";
import * as copy from "@/utils/vulnerabilityCopy.ts";

const KNOWN_VULNERABILITIES = "knownVulnerabilities";
const ATTEMPTED_STEPS: FurthestStep[] = ["refused", "failed", "signedIn"];
const ATTEMPT_FIELDS: Record<AttemptResult, keyof PortalAttempts> = {
  blocked: "blocked", userNotFound: "user_not_found", invalidPassword: "invalid_password",
  failedSecondFactor: "failed_second_factor", signedIn: "signed_in",
};
const FIGURE_LABELS: readonly (readonly [string, string])[] = [
  [copy.FIG_ADDRESSES, copy.TIP_ADDRESSES],
  [copy.FIG_NAMED, copy.TIP_NAMED],
  [copy.FIG_ATTEMPTS, copy.TIP_ATTEMPTS],
  [copy.KPI_COVERAGE, copy.TIP_COVERAGE],
];

export interface FigureView {
  readonly label: string;
  readonly tip: string;
  readonly value: string;
  readonly href: string | null;
  readonly caption: string | null;
}

export interface PortalSegment {
  readonly key: string;
  readonly label: string;
  readonly count: number | null;
  readonly href?: string;
  readonly blocked?: { readonly count: number; readonly href: string };
}

/** The overview, or null when it is not known: the views are missing, or the company has no summary row. */
export function knownOverview(data: PortalOverview | undefined): KnownPortalOverview | null {
  const hasSections = data?.coverage != null && data.figures != null && data.traffic != null;
  return data?.available === true && hasSections ? (data as KnownPortalOverview) : null;
}

/** The window's addresses and requests per CVE, or null while they are not known. */
export function windowCves(overview: KnownPortalOverview | null): ReadonlyMap<string, PortalCve> | null {
  if (overview === null || overview.cves === null) return null;
  return new Map(overview.cves.map((row) => [row.cve, row]));
}

function attemptTotal(attempts: PortalAttempts): number {
  return ATTEMPT_ORDER.reduce((sum, result) => sum + attempts[ATTEMPT_FIELDS[result]], 0);
}

function namedFigure(figures: PortalFigures): FigureView {
  return {
    label: copy.FIG_NAMED,
    tip: copy.TIP_NAMED,
    value: copy.countOrNotKnown(figures.named_addresses),
    href: figures.named_addresses === null ? null : sourcesHref({ named: true }),
    caption: figures.named_requests === null ? null : copy.requestsLabel(figures.named_requests),
  };
}

export function buildFigureViews(overview: KnownPortalOverview | null): FigureView[] {
  if (overview === null) {
    return FIGURE_LABELS.map(([label, tip]) => ({ label, tip, value: copy.NOT_KNOWN, href: null, caption: null }));
  }
  const { figures, coverage, traffic } = overview;
  return [
    { label: copy.FIG_ADDRESSES, tip: copy.TIP_ADDRESSES, value: copy.countOrNotKnown(figures.addresses),
      href: sourcesHref({}), caption: figures.requests === null ? null : copy.requestsLabel(figures.requests) },
    namedFigure(figures),
    { label: copy.FIG_ATTEMPTS, tip: copy.TIP_ATTEMPTS, value: copy.countOrNotKnown(attemptTotal(traffic.attempts)),
      href: sourcesHref({ furthestSteps: ATTEMPTED_STEPS }), caption: null },
    { label: copy.KPI_COVERAGE, tip: copy.TIP_COVERAGE,
      value: copy.coverageLabel(coverage.days_with_lines, coverage.window_days), href: null, caption: null },
  ];
}

function stepSegment(row: PortalStep): PortalSegment {
  const step = FURTHEST_STEP_ORDER.find((known) => known === row.step);
  if (step === undefined) {
    return { key: row.step, label: copy.stepLabel(row.step), count: row.addresses };
  }
  return {
    key: row.step,
    label: copy.stepLabel(row.step),
    count: row.addresses,
    href: sourcesHref({ furthestSteps: [step] }),
    blocked: { count: row.blocked_addresses, href: sourcesHref({ furthestSteps: [step], answers: ["blocked"] }) },
  };
}

export function buildStepSegments(overview: KnownPortalOverview | null): PortalSegment[] {
  if (overview === null) {
    return FURTHEST_STEP_ORDER.map((step) => ({
      key: step, label: copy.STEPS[step], count: null, href: sourcesHref({ furthestSteps: [step] }),
    }));
  }
  return overview.traffic.steps.map(stepSegment);
}

export function buildAttemptSegments(overview: KnownPortalOverview | null): PortalSegment[] {
  return ATTEMPT_ORDER.map((result) => ({
    key: result,
    label: copy.ATTEMPT_RESULTS[result],
    count: overview === null ? null : overview.traffic.attempts[ATTEMPT_FIELDS[result]],
    href: sourcesHref({ attempts: [result] }),
  }));
}

export function buildWhoSegments(overview: KnownPortalOverview | null): PortalSegment[] {
  const who = overview?.who ?? null;
  return WHO_ORDER.map((key) => ({
    key, label: copy.WHO[key], count: who === null ? null : who[key], href: sourcesHref({ who: [key] }),
  }));
}

export function groupHref(group: string): string {
  return sourcesHref({ groups: [group] });
}

export function itemHref(item: PortalItem): string {
  return item.kind === "cve" ? sourcesHref({ search: item.key }) : sourcesHref({ rules: [item.key] });
}

export function sharePercent(part: number, whole: number): number {
  return whole > 0 ? Math.min(100, Math.max(0, (part / whole) * 100)) : 0;
}

/** The your-product count on the known-vulnerabilities row; undefined on every other row. */
export function yourProductAddresses(overview: KnownPortalOverview | null, group: PortalGroup): number | null | undefined {
  if (overview === null || group.group !== KNOWN_VULNERABILITIES) return undefined;
  return overview.your_product_addresses;
}
